import { useState, useRef, useCallback, useEffect } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

/* ── Shared sub-components (receive s = translated sell keys) ── */

const Label = ({ children, required }) => (
  <label style={{
    display: 'block', fontFamily: FONT, fontSize: '13px',
    fontWeight: 600, color: NAVY, marginBottom: '8px', letterSpacing: '0.2px',
  }}>
    {children}{required && <span style={{ color: '#e53e3e', marginLeft: '3px' }}>*</span>}
  </label>
)

const FieldHint = ({ children }) => (
  <p style={{ fontFamily: FONT, fontSize: '12px', color: '#8a9ab0', margin: '6px 0 0', lineHeight: '1.4' }}>
    {children}
  </p>
)

const baseInput = {
  width: '100%', padding: '13px 16px', border: '1.5px solid #dde2ea',
  borderRadius: '10px', fontFamily: FONT, fontSize: '14px', color: NAVY,
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
}

const SectionCard = ({ title, icon, children, accent }) => (
  <div style={{
    background: '#fff', borderRadius: '16px',
    boxShadow: '0 2px 18px rgba(0,46,83,0.07)',
    padding: '36px 40px', marginBottom: '28px',
    borderTop: `4px solid ${accent || NAVY}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '10px',
        background: `${accent || NAVY}15`, display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0,
      }}>
        {icon}
      </div>
      <h3 style={{ fontFamily: FONT, fontWeight: 700, fontSize: '18px', color: NAVY, margin: 0 }}>
        {title}
      </h3>
    </div>
    {children}
  </div>
)

function DocUpload({ label, hint, id, accept = '.pdf,.jpg,.jpeg,.png', onChange, files, s }) {
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  
  const addFiles = (newFiles) => {
    const merged = [...(files || []), ...newFiles]
    onChange(merged)
  }
  
  const removeFile = (idx) => {
    onChange(files.filter((_, i) => i !== idx))
  }
  
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false); addFiles(Array.from(e.dataTransfer.files))
  }, [addFiles])
  
  const hasFiles = files && files.length > 0
  
  return (
    <div>
      <Label required>{label}</Label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? GOLD : '#c8d4e0'}`,
          borderRadius: '12px', padding: '24px 20px', textAlign: 'center',
          cursor: 'pointer', transition: 'all 0.2s',
          background: dragging ? `${GOLD}08` : '#f8fafb',
        }}
      >
        <input ref={inputRef} id={id} type="file" accept={accept} multiple style={{ display: 'none' }} onChange={(e) => addFiles(Array.from(e.target.files))} />
        <div style={{ fontSize: '28px', marginBottom: '10px' }}>📎</div>
        <div style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 500, color: NAVY }}>
          {hasFiles ? `${files.length} file${files.length > 1 ? 's' : ''} added` : s.dragDrop} <span style={{ color: GOLD, textDecoration: 'underline' }}>{hasFiles ? s.browse : s.browse}</span>
        </div>
        <div style={{ fontFamily: FONT, fontSize: '12px', color: '#8a9ab0', marginTop: '6px' }}>{s.accepted}</div>
      </div>
      
      {hasFiles && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '14px' }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #dde2ea', padding: '10px 14px', borderRadius: '8px' }}>
              <div style={{ fontFamily: FONT, fontSize: '13px', color: NAVY, fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>📄</span>
                {f.name} <span style={{ color: '#8a9ab0', fontWeight: 400 }}>({(f.size / 1024).toFixed(1)} KB)</span>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i) }} style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#fca5a5', color: '#dc2626', border: 'none', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Remove">×</button>
            </div>
          ))}
        </div>
      )}
      {hint && <FieldHint>{hint}</FieldHint>}
    </div>
  )
}

function PhotoUpload({ photos, onChange, s }) {
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const addPhotos = (newFiles) => {
    const merged = [...photos]
    newFiles.forEach((f) => { if (merged.length >= 20) return; merged.push({ file: f, preview: URL.createObjectURL(f) }) })
    onChange(merged)
  }
  const removePhoto = (idx) => {
    const updated = photos.filter((_, i) => i !== idx)
    URL.revokeObjectURL(photos[idx].preview)
    onChange(updated)
  }
  const setCover = (idx) => {
    if (idx === 0) return;
    const newPhotos = [...photos];
    const [selected] = newPhotos.splice(idx, 1);
    newPhotos.unshift(selected);
    onChange(newPhotos);
  }
  return (
    <div>
      <Label required>{s.photosLabel}</Label>
      <FieldHint>{s.photosHint}</FieldHint>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addPhotos(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'))) }}
        onClick={() => inputRef.current?.click()}
        style={{ border: `2px dashed ${dragging ? GOLD : '#c8d4e0'}`, borderRadius: '14px', padding: '40px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', marginTop: '14px', background: dragging ? `${GOLD}08` : '#f8fafb' }}
      >
        <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => addPhotos(Array.from(e.target.files))} />
        <div style={{ fontSize: '42px', marginBottom: '12px' }}>🖼️</div>
        <div style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 600, color: NAVY }}>
          {photos.length > 0 ? s.photosAdded(photos.length) : s.dropPhotos}
        </div>
        <div style={{ fontFamily: FONT, fontSize: '13px', color: '#8a9ab0', marginTop: '6px' }}>
          {s.orClickBrowse} &nbsp;·&nbsp; {s.upTo20}
        </div>
      </div>
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px', marginTop: '20px' }}>
          {photos.map((p, idx) => (
            <div key={idx} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '4/3' }}>
              <img src={p.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {idx === 0 && <div style={{ position: 'absolute', top: '6px', left: '6px', background: GOLD, color: '#fff', fontSize: '10px', fontFamily: FONT, fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>COVER</div>}
              {idx > 0 && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setCover(idx) }} style={{ position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,46,83,0.85)', color: '#fff', fontSize: '10px', fontFamily: FONT, fontWeight: 600, padding: '4px 10px', borderRadius: '4px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Set Cover
                </button>
              )}
              <button type="button" onClick={(e) => { e.stopPropagation(); removePhoto(idx) }} style={{ position: 'absolute', top: '6px', right: '6px', width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Remove">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const PROPERTY_TYPES = ['Apartment', 'Villa', 'Townhouse', 'Penthouse', 'Office', 'Warehouse', 'Hotel', 'Plot', 'Building', 'Other']
const EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain']
const FURNISHING_KEYS = [
  { key: 'Furnished',      label: (s) => s.furnishing_furnished  || 'Furnished'      },
  { key: 'Semi-Furnished', label: (s) => s.furnishing_semi       || 'Semi-Furnished'  },
  { key: 'Unfurnished',    label: (s) => s.furnishing_no         || 'Unfurnished'     },
]

function ListYourPropertyForm({ s }) {
  const [listing, setListing] = useState('sell')
  const [form, setForm] = useState({ ownerName: '', email: '', phone: '', whatsapp: '', propertyType: '', emirate: '', community: '', subCommunity: '', bedrooms: '', bathrooms: '', size: '', floor: '', furnishing: '', askingPrice: '', currency: 'AED', description: '', completionStatus: 'ready', youtubeLink: '' })
  const [titleDeed, setTitleDeed] = useState([])
  const [floorPlan, setFloorPlan] = useState([])
  const [passport, setPassport] = useState([])
  const [photos, setPhotos] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }))
  const clearError = (key) => { if (errors[key]) setErrors(p => { const n = { ...p }; delete n[key]; return n }) }
  const inputStyle = (key) => ({ ...baseInput, borderColor: errors[key] ? '#e53e3e' : '#dde2ea', boxShadow: errors[key] ? '0 0 0 3px rgba(229,62,62,0.12)' : 'none' })

  const validate = () => {
    const errs = {}
    if (!form.ownerName.trim()) errs.ownerName = s.errName
    if (!form.email.trim() || !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(form.email)) errs.email = s.errEmail
    if (!form.phone.trim()) errs.phone = s.errPhone
    if (!form.propertyType) errs.propertyType = s.errPropType
    if (!form.emirate) errs.emirate = s.errEmirate
    if (!form.askingPrice.trim()) errs.askingPrice = listing === 'sell' ? s.errPrice : s.errRent
    if (photos.length === 0) errs.photos = s.errPhotos
    return errs
  }

  const resetForm = () => {
    setSubmitted(false); setPhotos([]); setTitleDeed([]); setFloorPlan([]); setPassport([])
    setForm({ ownerName: '', email: '', phone: '', whatsapp: '', propertyType: '', emirate: '', community: '', subCommunity: '', bedrooms: '', bathrooms: '', size: '', floor: '', furnishing: '', askingPrice: '', currency: 'AED', description: '', completionStatus: 'ready', youtubeLink: '' })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) { setErrors(errs); document.querySelector('[data-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return }
    setSubmitting(true)
    try {
      // Convert first 3 photos to base64 to keep payload manageable
      const toBase64 = (file) => new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (ev) => resolve({ name: file.name, data: ev.target.result })
        reader.readAsDataURL(file)
      })
      const photoSlice = photos.slice(0, 3)
      const photoBase64 = await Promise.all(photoSlice.map(p => toBase64(p.file).then(r => r.data)))
      
      const docs = {
        titleDeed: await Promise.all(titleDeed.map(f => toBase64(f))),
        floorPlan: await Promise.all(floorPlan.map(f => toBase64(f))),
        passport: await Promise.all(passport.map(f => toBase64(f)))
      }

      const res = await fetch('/api/admin/listings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing, form, photos: photoBase64, documents: docs }),
      })
      if (!res.ok) throw new Error('Submission failed')
      setSubmitted(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      alert('Something went wrong — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div style={{ background: '#f4f7fb', padding: '80px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: '20px', padding: '60px 50px', textAlign: 'center', maxWidth: '540px', width: '100%', boxShadow: '0 8px 40px rgba(0,46,83,0.12)' }}>
          <div style={{ fontSize: '56px', marginBottom: '20px' }}>🎉</div>
          <h2 style={{ fontFamily: FONT, fontSize: '28px', fontWeight: 700, color: NAVY, margin: '0 0 14px' }}>{s.successTitle}</h2>
          <p style={{ fontFamily: FONT, fontSize: '15px', color: '#5a6577', lineHeight: '1.7', margin: '0 0 30px' }}>
            {s.successDesc} <strong>{s.successHours}</strong>.
          </p>
          <button onClick={resetForm} style={{ background: NAVY, color: '#fff', fontFamily: FONT, fontSize: '15px', fontWeight: 600, padding: '14px 36px', borderRadius: '10px', border: 'none', cursor: 'pointer' }}>
            {s.submitAnother}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div id="list-property" style={{ background: '#f4f7fb', padding: '80px 20px' }}>
      <div style={{ maxWidth: '860px', margin: '0 auto' }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <span style={{ display: 'inline-block', background: `${GOLD}18`, color: GOLD, fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px', padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase', border: `1px solid ${GOLD}35`, marginBottom: '12px' }}>{s.formBadge}</span>
          <h2 style={{ fontFamily: FONT, fontSize: 'clamp(24px,3vw,38px)', fontWeight: 700, color: NAVY, margin: '0 0 12px' }}>{s.formHeading}</h2>
          <p style={{ fontFamily: FONT, fontSize: '15px', color: '#6b7a8d', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto' }}>{s.formSub}</p>
        </div>

        {/* Sell / Rent toggle */}
        <div style={{ display: 'flex', background: '#fff', borderRadius: '14px', padding: '6px', boxShadow: '0 2px 14px rgba(0,46,83,0.08)', marginBottom: '32px', maxWidth: '400px' }}>
          {[['sell', s.tabSell], ['rent', s.tabRent]].map(([opt, label]) => (
            <button key={opt} type="button" onClick={() => setListing(opt)} style={{ flex: 1, padding: '13px', border: 'none', borderRadius: '10px', cursor: 'pointer', fontFamily: FONT, fontSize: '15px', fontWeight: 600, transition: 'all 0.2s', background: listing === opt ? NAVY : 'transparent', color: listing === opt ? '#fff' : '#8a9ab0' }}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <style>{`
            .sr-input:focus { border-color: ${NAVY} !important; box-shadow: 0 0 0 3px rgba(0,46,83,0.12) !important; }
            .sr-select:focus { border-color: ${NAVY} !important; box-shadow: 0 0 0 3px rgba(0,46,83,0.12) !important; }
            .sr-select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23002E53' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 36px !important; }
            @media(max-width:600px){ .sr-two-col { grid-template-columns: 1fr !important; } }
          `}</style>

          {/* ── Contact Details ── */}
          <SectionCard title={s.sectionContact} icon="👤" accent={NAVY}>
            <div className="sr-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div data-error={errors.ownerName ? true : undefined}>
                <Label required>{s.fullName}</Label>
                <input className="sr-input" type="text" placeholder="e.g. Mohammed Al Rashidi" value={form.ownerName} onChange={e => { set('ownerName', e.target.value); clearError('ownerName') }} style={inputStyle('ownerName')} />
                {errors.ownerName && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px' }}>{errors.ownerName}</p>}
              </div>
              <div data-error={errors.email ? true : undefined}>
                <Label required>{s.email}</Label>
                <input className="sr-input" type="email" placeholder="you@example.com" value={form.email} onChange={e => { set('email', e.target.value); clearError('email') }} style={inputStyle('email')} />
                {errors.email && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px' }}>{errors.email}</p>}
              </div>
              <div data-error={errors.phone ? true : undefined}>
                <Label required>{s.phone}</Label>
                <input className="sr-input" type="tel" placeholder="+971 50 123 4567" value={form.phone} onChange={e => { set('phone', e.target.value); clearError('phone') }} style={inputStyle('phone')} />
                {errors.phone && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px' }}>{errors.phone}</p>}
              </div>
              <div>
                <Label>{s.whatsapp}</Label>
                <input className="sr-input" type="tel" placeholder="+971 50 123 4567" value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} style={baseInput} />
              </div>
            </div>
          </SectionCard>

          {/* ── Property Details ── */}
          <SectionCard title={s.sectionProperty} icon="🏢" accent={GOLD}>
            <div className="sr-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div data-error={errors.propertyType ? true : undefined}>
                <Label required>{s.propertyType}</Label>
                <select className="sr-select sr-input" value={form.propertyType} onChange={e => { set('propertyType', e.target.value); clearError('propertyType') }} style={inputStyle('propertyType')}>
                  <option value="">{s.selectType}</option>
                  {PROPERTY_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                </select>
                {errors.propertyType && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px' }}>{errors.propertyType}</p>}
              </div>
              <div data-error={errors.emirate ? true : undefined}>
                <Label required>{s.emirate}</Label>
                <select className="sr-select sr-input" value={form.emirate} onChange={e => { set('emirate', e.target.value); clearError('emirate') }} style={inputStyle('emirate')}>
                  <option value="">{s.selectEmirate}</option>
                  {EMIRATES.map(em => <option key={em} value={em}>{em}</option>)}
                </select>
                {errors.emirate && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px' }}>{errors.emirate}</p>}
              </div>
              <div><Label>{s.community}</Label><input className="sr-input" type="text" placeholder="e.g. Downtown Dubai" value={form.community} onChange={e => set('community', e.target.value)} style={baseInput} /></div>
              <div><Label>{s.subCommunity}</Label><input className="sr-input" type="text" placeholder="e.g. Burj Khalifa" value={form.subCommunity} onChange={e => set('subCommunity', e.target.value)} style={baseInput} /></div>
              <div>
                <Label>{s.bedrooms}</Label>
                <select className="sr-select sr-input" value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)} style={baseInput}>
                  <option value="">{s.select}</option>
                  {['Studio','1','2','3','4','5','6','7+'].map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <Label>{s.bathrooms}</Label>
                <select className="sr-select sr-input" value={form.bathrooms} onChange={e => set('bathrooms', e.target.value)} style={baseInput}>
                  <option value="">{s.select}</option>
                  {['1','2','3','4','5','6+'].map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div><Label>{s.areaSqFt}</Label><input className="sr-input" type="number" placeholder="e.g. 1200" value={form.size} onChange={e => set('size', e.target.value)} style={baseInput} min="0" /></div>
              <div><Label>{s.floor}</Label><input className="sr-input" type="text" placeholder="e.g. 12" value={form.floor} onChange={e => set('floor', e.target.value)} style={baseInput} /></div>
              <div>
                <Label>{s.furnishing}</Label>
                <select className="sr-select sr-input" value={form.furnishing} onChange={e => set('furnishing', e.target.value)} style={baseInput}>
                  <option value="">{s.select}</option>
                  {FURNISHING_KEYS.map(({ key }) => <option key={key} value={key}>{key}</option>)}
                </select>
              </div>
              <div>
                <Label>{s.completion}</Label>
                <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                  {[['ready', s.ready], ['offplan', s.offPlan]].map(([val, lbl]) => (
                    <label key={val} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderRadius: '10px', cursor: 'pointer', border: `1.5px solid ${form.completionStatus === val ? NAVY : '#dde2ea'}`, background: form.completionStatus === val ? `${NAVY}08` : '#fff', fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY, transition: 'all 0.2s' }}>
                      <input type="radio" name="completion" value={val} checked={form.completionStatus === val} onChange={() => set('completionStatus', val)} style={{ accentColor: NAVY }} />{lbl}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Price */}
            <div style={{ marginTop: '20px' }}>
              <Label required>{listing === 'sell' ? s.askingPrice : s.rentAmount}</Label>
              <div style={{ display: 'flex', gap: '10px' }} data-error={errors.askingPrice ? true : undefined}>
                <select className="sr-select sr-input" value={form.currency} onChange={e => set('currency', e.target.value)} style={{ ...baseInput, width: '110px', flexShrink: 0 }}>
                  {['AED','USD','EUR','GBP'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input className="sr-input" type="number" placeholder="e.g. 1,500,000" value={form.askingPrice} onChange={e => { set('askingPrice', e.target.value); clearError('askingPrice') }} style={{ ...inputStyle('askingPrice'), flex: 1 }} min="0" />
              </div>
              {errors.askingPrice && <p style={{ color: '#e53e3e', fontSize: '12px', fontFamily: FONT, marginTop: '6px' }}>{errors.askingPrice}</p>}
              <FieldHint>{s.priceHint}</FieldHint>
            </div>

            {/* YouTube Link */}
            <div style={{ marginTop: '20px' }}>
              <Label>{s.youtubeLink || 'YouTube Video Link'}</Label>
              <input className="sr-input" type="url" placeholder="https://www.youtube.com/watch?v=..." value={form.youtubeLink} onChange={e => set('youtubeLink', e.target.value)} style={baseInput} />
              <FieldHint>{s.youtubeHint || 'Add a YouTube link to showcase your property (optional)'}</FieldHint>
            </div>

            {/* Description */}
            <div style={{ marginTop: '20px' }}>
              <Label>{s.description}</Label>
              <textarea className="sr-input" rows={5} placeholder={s.descPlaceholder} value={form.description} onChange={e => set('description', e.target.value)} style={{ ...baseInput, resize: 'vertical', minHeight: '120px', lineHeight: '1.6' }} />
            </div>
          </SectionCard>

          {/* ── Legal Documents ── */}
          <SectionCard title={s.sectionDocs} icon="📋" accent="#6366f1">
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#5a6577', marginTop: 0, marginBottom: '28px', lineHeight: '1.7' }}>{s.docsIntro}</p>
            <div className="sr-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <DocUpload label={s.titleDeed} id="title-deed" hint={s.titleDeedHint} files={titleDeed} onChange={setTitleDeed} s={s} />
              <DocUpload label={s.floorPlan} id="floor-plan" hint={s.floorPlanHint} files={floorPlan} onChange={setFloorPlan} s={s} />
            </div>
            <div style={{ marginTop: '24px', maxWidth: '50%' }}>
              <DocUpload label={s.passport} id="passport-copy" hint={s.passportHint} accept=".pdf,.jpg,.jpeg,.png" files={passport} onChange={setPassport} s={s} />
            </div>
            <div style={{ marginTop: '20px', background: '#f0f4ff', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '18px', marginTop: '1px' }}>🔒</span>
              <p style={{ fontFamily: FONT, fontSize: '13px', color: '#4a5568', margin: 0, lineHeight: '1.6' }}>
                <strong>{s.privacy}</strong>
              </p>
            </div>
          </SectionCard>

          {/* ── Photos ── */}
          <SectionCard title={s.sectionPhotos} icon="📸" accent={GOLD}>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#5a6577', marginTop: 0, marginBottom: '8px', lineHeight: '1.7' }}>{s.photosIntro}</p>
            <PhotoUpload photos={photos} onChange={setPhotos} s={s} />
            {errors.photos && <p style={{ color: '#e53e3e', fontSize: '13px', fontFamily: FONT, marginTop: '10px' }}>{errors.photos}</p>}
          </SectionCard>

          {/* ── Submit ── */}
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <button type="submit" disabled={submitting} style={{ background: submitting ? '#8a9ab0' : NAVY, color: '#fff', fontFamily: FONT, fontSize: '16px', fontWeight: 700, padding: '17px 60px', borderRadius: '12px', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer', transition: 'all 0.25s', boxShadow: submitting ? 'none' : `0 8px 24px rgba(0,46,83,0.3)`, letterSpacing: '0.3px' }}
              onMouseEnter={e => { if (!submitting) e.currentTarget.style.transform = 'translateY(-2px)' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)' }}>
              {submitting ? s.submitting : (listing === 'sell' ? s.submitSale : s.submitRental)}
            </button>
            <p style={{ fontFamily: FONT, fontSize: '13px', color: '#8a9ab0', marginTop: '14px' }}>{s.termsNotice}</p>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ── Approved Listings Section ── */
function ApprovedListings({ s }) {
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/admin/listings/approved')
      .then(r => r.json())
      .then(data => { setListings(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  if (loading || listings.length === 0) return null

  return (
    <div style={{ background: '#fff', padding: '80px 20px' }}>
      <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <span style={{ display: 'inline-block', background: `${GOLD}18`, color: GOLD, fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px', padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase', border: `1px solid ${GOLD}35`, marginBottom: '12px' }}>Available Now</span>
          <h2 style={{ fontFamily: FONT, fontSize: 'clamp(24px,3vw,38px)', fontWeight: 700, color: NAVY, margin: '0 0 12px' }}>Featured Listings</h2>
          <p style={{ fontFamily: FONT, fontSize: '15px', color: '#6b7a8d', lineHeight: 1.7, maxWidth: '520px', margin: '0 auto' }}>Properties listed directly by owners — reviewed and approved by our team.</p>
        </div>

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
          {listings.map(l => {
            const f = l.form || {}
            return (
              <div key={l.id} style={{ background: '#fff', borderRadius: '20px', boxShadow: '0 4px 24px rgba(0,46,83,0.1)', overflow: 'hidden', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer' }}
                onClick={() => {
                  const name = `${f.propertyType || 'Property'} in ${f.emirate || 'UAE'}`
                  const slug = name.toLowerCase().trim().replace(/[\s\W-]+/g, '-')
                  window.location.href = `/properties/detail/${slug}/${l.id}`
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,46,83,0.18)' }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,46,83,0.1)' }}
              >
                {/* Photo */}
                <div style={{ height: '200px', background: `${NAVY}10`, overflow: 'hidden', position: 'relative' }}>
                  {l.coverPhoto ? (
                    <img src={l.coverPhoto} alt={f.propertyType} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '48px' }}>🏠</div>
                  )}
                  {/* Badges */}
                  <div style={{ position: 'absolute', top: '12px', left: '12px', display: 'flex', gap: '6px' }}>
                    <span style={{ background: NAVY, color: '#fff', fontFamily: FONT, fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '50px', textTransform: 'uppercase' }}>{l.listing}</span>
                    {f.completionStatus && <span style={{ background: GOLD, color: '#fff', fontFamily: FONT, fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '50px' }}>{f.completionStatus === 'ready' ? 'Ready' : 'Off-Plan'}</span>}
                  </div>
                </div>

                {/* Details */}
                <div style={{ padding: '20px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                    <h3 style={{ fontFamily: FONT, fontSize: '17px', fontWeight: 700, color: NAVY, margin: 0, lineHeight: 1.3 }}>
                      {f.propertyType} in {f.emirate}
                    </h3>
                    <div style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 800, color: GOLD, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {f.currency || 'AED'} {Number(f.askingPrice || 0).toLocaleString()}
                    </div>
                  </div>

                  {f.community && (
                    <p style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7a8d', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      📍 {f.community}{f.subCommunity ? `, ${f.subCommunity}` : ''}
                    </p>
                  )}

                  {/* Specs */}
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {f.bedrooms && <span style={{ fontFamily: FONT, fontSize: '12px', color: '#4a5568', background: '#f0f4f8', padding: '4px 10px', borderRadius: '6px' }}>🛏 {f.bedrooms} Bed</span>}
                    {f.bathrooms && <span style={{ fontFamily: FONT, fontSize: '12px', color: '#4a5568', background: '#f0f4f8', padding: '4px 10px', borderRadius: '6px' }}>🚿 {f.bathrooms} Bath</span>}
                    {f.size && <span style={{ fontFamily: FONT, fontSize: '12px', color: '#4a5568', background: '#f0f4f8', padding: '4px 10px', borderRadius: '6px' }}>📐 {Number(f.size).toLocaleString()} sqft</span>}
                    {f.furnishing && <span style={{ fontFamily: FONT, fontSize: '12px', color: '#4a5568', background: '#f0f4f8', padding: '4px 10px', borderRadius: '6px' }}>{f.furnishing}</span>}
                  </div>

                  {/* Contact */}
                  <div style={{ borderTop: '1px solid #f0f2f5', paddingTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY }}>{f.ownerName}</div>
                      <div style={{ fontFamily: FONT, fontSize: '12px', color: '#8a9ab0' }}>{f.phone}</div>
                    </div>
                    <a
                      href={`https://wa.me/${(f.whatsapp || f.phone || '').replace(/[^0-9+]/g, '')}?text=Hi, I'm interested in your ${f.propertyType} in ${f.emirate}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#25D366', color: '#fff', fontFamily: FONT, fontSize: '12px', fontWeight: 700, padding: '8px 14px', borderRadius: '8px', textDecoration: 'none', flexShrink: 0 }}
                    >
                      💬 WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ── Main Page ── */
export default function SellRentPage() {
  const { t } = useLanguage()
  const s = t('sell')   // whole sell object — all sub-components receive this

  return (
    <div style={{ fontFamily: FONT }}>
      <Navbar />

      {/* ── Hero ── */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 60%, #0a2d4a 100%)`,
        padding: '150px 20px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '380px', height: '380px', borderRadius: '50%', background: `${GOLD}15`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '260px', height: '260px', borderRadius: '50%', background: `${GOLD}10`, pointerEvents: 'none' }} />

        <span style={{ display: 'inline-block', background: `${GOLD}20`, color: GOLD, fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', padding: '6px 18px', borderRadius: '50px', marginBottom: '18px', textTransform: 'uppercase', border: `1px solid ${GOLD}40` }}>
          {s.heroBadge}
        </span>
        <h1 style={{ fontFamily: FONT, fontSize: 'clamp(30px, 4vw, 52px)', fontWeight: 700, color: '#fff', margin: '0 0 16px', letterSpacing: '-0.5px', lineHeight: '1.2' }}>
          {s.heroHeadline} <br /><span style={{ color: GOLD }}>{s.heroAccent}</span>
        </h1>
        <p style={{ fontFamily: FONT, fontSize: '16px', color: 'rgba(255,255,255,0.72)', maxWidth: '580px', margin: '0 auto 36px', lineHeight: '1.7' }}>
          {s.heroSub}
        </p>
      </div>

      {/* ── Approved Listings (above the form) ── */}
      <ApprovedListings s={s} />

      <ListYourPropertyForm s={s} />

      <Footer />
    </div>
  )
}
